"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchLiveEventSnapshots } from "@/features/live-data/liveEventsClient";
import type { LiveEventSnapshot } from "@/features/live-data/snapshots";
import type { MatchRow } from "@/utilities/data/competition/derive/types";
import { IN_PLAY_STATUSES } from "./liveRow";
const LIVE_REFRESH_INTERVAL_MS = 30000;
/** Polling starts this long before kickoff. */
const PRE_KICKOFF_POLL_MS = 2 * 60 * 1000;
/** A row still non-terminal this long after kickoff is treated as stale data and not polled. */
const MAX_POLL_AFTER_KICKOFF_MS = 4 * 60 * 60 * 1000;
const MIN_TIMER_DELAY_MS = 1000;
/** setTimeout fires immediately for delays above 2^31 - 1 ms; longer waits re-arm on expiry. */
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

/** The FansUnited event id `/api/live/events` serves (what snapshots are keyed by). */
export function matchEventId(match: MatchRow): string {
  return match.fansUnitedMatchId || match.id;
}
const TERMINAL_STATUSES = new Set(["FINISHED", "CANCELLED", "POSTPONED", "ABANDONED"]);
function effectiveStatusType(match: MatchRow, snapshots: ReadonlyMap<string, LiveEventSnapshot>): string | undefined {
  return (snapshots.get(matchEventId(match))?.status ?? match.status)?.type;
}

/**
 * Ids that need a live refresh. The effective status is the latest snapshot's
 * when present, else the stored one: terminal is not polled, nor is anything
 * that kicked off more than 4 hours ago; in-play is polled, and anything else
 * (not started / unknown) is polled once kickoff is within 2 minutes or
 * already reached, until a LIVE or terminal status arrives.
 */
export function selectMatchIdsNeedingPolling(matches: readonly MatchRow[], snapshots: ReadonlyMap<string, LiveEventSnapshot>, now: Date = new Date()): string[] {
  return matches.filter(match => {
    const type = effectiveStatusType(match, snapshots);
    if (type && TERMINAL_STATUSES.has(type)) return false;
    const kickoff = match.timing?.kickoffTime ? new Date(match.timing.kickoffTime).getTime() : null;
    if (kickoff !== null && now.getTime() - kickoff > MAX_POLL_AFTER_KICKOFF_MS) return false;
    if (type && IN_PLAY_STATUSES.has(type)) return true;
    if (kickoff === null) return false;
    return kickoff - now.getTime() <= PRE_KICKOFF_POLL_MS;
  }).map(matchEventId);
}

/**
 * Milliseconds until the earliest non-terminal row enters its pre-kickoff
 * polling window (kickoff − 2 min), clamped to [1 s, max timer delay]; null
 * when no row's window lies in the future.
 */
export function nextKickoffCheckDelayMs(matches: readonly MatchRow[], snapshots: ReadonlyMap<string, LiveEventSnapshot>, now: Date): number | null {
  let earliest: number | null = null;
  for (const match of matches) {
    const type = effectiveStatusType(match, snapshots);
    if (type && (TERMINAL_STATUSES.has(type) || IN_PLAY_STATUSES.has(type))) continue;
    if (!match.timing?.kickoffTime) continue;
    const windowStart = new Date(match.timing.kickoffTime).getTime() - PRE_KICKOFF_POLL_MS;
    if (windowStart <= now.getTime()) continue;
    if (earliest === null || windowStart < earliest) earliest = windowStart;
  }
  if (earliest === null) return null;
  return Math.min(Math.max(earliest - now.getTime(), MIN_TIMER_DELAY_MS), MAX_TIMER_DELAY_MS);
}

/**
 * Polls live snapshots for the given matches while at least one needs it.
 * With nothing to poll, one timer re-checks the selection when the next row
 * enters its pre-kickoff window. Snapshots are keyed by `matchEventId`.
 */
export function useRoundLiveSnapshots(matches: MatchRow[]): Map<string, LiveEventSnapshot> {
  const [snapshots, setSnapshots] = useState<Map<string, LiveEventSnapshot>>(new Map());
  const [tick, setTick] = useState(0);
  const pollingIds = useMemo(() => selectMatchIdsNeedingPolling(matches, snapshots, new Date()),
  // `tick` re-runs the selection at the next kickoff window with a fresh `now`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [matches, snapshots, tick]);
  const pollingIdsKey = useMemo(() => [...pollingIds].sort().join(","), [pollingIds]);
  const runRefresh = useCallback(async () => {
    if (pollingIds.length === 0) return;
    const fresh = await fetchLiveEventSnapshots(pollingIds);
    if (fresh.length === 0) return;
    setSnapshots(prev => {
      const next = new Map(prev);
      for (const snap of fresh) next.set(snap.id, snap);
      return next;
    });
  }, [pollingIds]);
  useEffect(() => {
    if (pollingIds.length === 0) return;
    runRefresh();
    const intervalId = setInterval(runRefresh, LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingIdsKey]);
  useEffect(() => {
    if (pollingIds.length > 0) return;
    const delay = nextKickoffCheckDelayMs(matches, snapshots, new Date());
    if (delay === null) return;
    const timeoutId = setTimeout(() => setTick(t => t + 1), delay);
    return () => clearTimeout(timeoutId);
  }, [pollingIds, matches, snapshots]);
  return snapshots;
}